import { Fragment, useMemo, useRef, useState } from 'react';

import useBreakpoints from '@snowit/hooks/useBreakpoints';
import Portal from '@snowit/hooks/usePortals';
import useAuth from '@snowit/system/useAuth';
import useLanguage from '@snowit/system/useLanguage';
import { Avatar, Icon } from '@snowit/ui';
import { m } from '@snowit/ui/utils/Motion';
import executer from '@snowit/utilities/executer';
import chroma from '@snowit/utils/chroma';
import { useRouter } from '@snowit/wouter';

import cx from 'classnames';
import s from './UserMenu.module.scss';

type UserMenuProps = { className?: string };

const UserMenu = (props: UserMenuProps) => {
  const { className } = props;
  const { t } = useLanguage();
  const { authDisplayName, authUID, signOut, user } = useAuth();
  const { minWidth } = useBreakpoints();
  const { nav } = useRouter();
  const menu = [
    {
      key: 'settings',
      label: t('lzy.action.settings'),
      onClick: () => nav('settings'),
    },
    {
      key: 'logout',
      label: t('lzy.action.logout'),
      onClick: signOut,
    },
  ];

  const avatar = useMemo(() => {
    // const by = getDoneBy(user.uid);
    const [name, ...surname] = (authDisplayName || authUID).split(' ');
    return {
      photoURL: user?.photoURL,
      name: name || '',
      surname: surname ? surname.join(' ') : '',
      displayName: authDisplayName,
    };
  }, [user]);

  const parent = useRef(null);

  const [showDropdown, setShowDropdown] = useState(false);

  const handleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const handleItemClick = (item) => {
    executer(item.onClick, item);
    setShowDropdown(false);
  };

  let motionVariants = {};

  if (minWidth.sm) {
    motionVariants = {
      open: { opacity: 1, height: 'auto' },
      collapsed: { opacity: 0, height: 0 },
    };
  } else {
    motionVariants = {
      open: { opacity: 1, translateX: '0%' },
      collapsed: { opacity: 1, translateX: '100%' },
    };
  }

  const renderUserMenuItem = (item) => {
    if (item.hide) return null;

    if (item.divider) {
      return <li className={s.divider} key={item.key} />;
    }

    return (
      <li
        className={cx(s.item, {
          [s.itemEvidence]: item.key === 'logout',
        })}
        key={item.key}
        onClick={() => handleItemClick(item)}
      >
        <a onClick={() => handleItemClick(item)}>{item.label}</a>
      </li>
    );
  };

  const renderUserMenuButton = () => {
    const [color, text] = chroma.seedColor(authUID, { preset: 'avatar' });
    return (
      <div
        className={cx(s.userMenu, className)}
        // id={id}
        onClick={handleDropdown}
        ref={parent}
      >
        <Icon
          className={s.icon}
          name={showDropdown ? 'close' : 'menu'}
          sizeV2={0.875}
          color={'text'}
        />
        <Avatar className={s.avatar} user={avatar} color={color} textColor={text} />
      </div>
    );
  };

  const Dropdown = (
    <m.ul
      // positionTransition
      className={s.dropDown}
      initial='collapsed'
      animate='open'
      exit='collapsed'
      variants={motionVariants}
      transition={{
        type: 'tween',
        duration: 0.25,
        ease: [0.215, 0.61, 0.355, 1],
      }}
    >
      {menu.map(renderUserMenuItem)}
    </m.ul>
  );

  return (
    <Fragment>
      {renderUserMenuButton()}
      <Portal
        id={'user_menu'}
        className={s.portal}
        mounted={showDropdown}
        content={Dropdown}
        bindTo={parent.current}
        position={'right'}
        onClose={() => setShowDropdown(false)}
        fullscreen={!minWidth.sm}
      />
    </Fragment>
  );
};

export default UserMenu;
